/**
 * URL Interceptor for Schema-Specific API Function Redirection
 *
 * Intercepts axios requests and rewrites URLs for configured schemas
 * to route through API functions instead of standard document endpoints.
 * Routing is keyed by both the schema name and the HTTP method, so each
 * verb can be dispatched to its own backend function.
 */

import type { InternalAxiosRequestConfig } from "axios";

import { HttpMethod, SchemaRedirectConfig } from "./schemaRedirects";

/**
 * Creates an axios request interceptor that rewrites document URLs
 * for configured schemas to route through API functions.
 *
 * URL Pattern: /data/v1/{schema}/documents{optional-path}
 * Rewritten to: /tasks/v1/api/{functionName}/documents{optional-path}
 * where {functionName} is resolved from (schema, HTTP method).
 *
 * Examples (with schemaRedirects.patient.{GET,PUT} configured):
 * - GET /data/v1/patient/documents     -> /tasks/v1/api/patients-read/documents
 * - PUT /data/v1/patient/documents/123 -> /tasks/v1/api/patients-update/documents/123
 *
 * Throws if a schema is configured but the request's HTTP method has
 * no mapping, to surface misconfiguration at the call site.
 */
export const createUrlInterceptor = (redirects: SchemaRedirectConfig, enableLogging = false) => {
  return (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const originalUrl = config.url || "";

    const match = originalUrl.match(/^\/data\/v1\/([^/]+)\/documents(.*)/);
    if (!match) return config;

    const [, schemaName, restOfPath] = match;
    const schemaConfig = redirects[schemaName];
    if (!schemaConfig) return config;

    const method = (config.method?.toUpperCase() ?? "GET") as HttpMethod;
    const functionName = schemaConfig[method];
    if (!functionName) {
      throw new Error(
        `urlInterceptor: no redirect mapping for ${method} on schema "${schemaName}" (url: ${originalUrl})`
      );
    }

    const newUrl = `/tasks/v1/api/${functionName}/documents${restOfPath}`;
    config.url = newUrl;

    if (enableLogging) {
      // eslint-disable-next-line no-console
      console.log(`🔄 URL Redirect: ${method} ${originalUrl} -> ${newUrl}`, {
        schema: schemaName,
        method,
        functionName,
      });
    }

    return config;
  };
};
